import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeftRight,
  Check,
  Languages,
  LogOut,
  MoreVertical,
  Palette,
  Settings,
} from 'lucide-react';
import type { MenuProps } from 'antd';

import { cn } from '../../lib/cn';
import { DropdownMenu } from '../ui';
import { NGON_NGU, NHAN_NGON_NGU, useNgonNgu, useT } from '../../i18n';
import { useAuthStore } from '../../stores/auth';
import { oidcEnabled } from '../../lib/auth/oidc';
import { usePhamVi } from '../../hooks/usePhamVi';
import { TIEN_TO, tach } from '../../lib/duong-dan-cong';

/**
 * Menu tài khoản ở đáy thanh điều hướng.
 *
 * Gom bốn hành động — ngôn ngữ, cài đặt tài khoản, tuỳ chỉnh giao diện, đăng
 * xuất — vào một nút `⋮`, đúng luật của ADR-UI-0005: **dãy nút chỉ-có-icon thì
 * gom vào menu tràn**.
 *
 * Vì sao phải gom: thanh rộng 260px, trừ lề và avatar còn ~200px cho **cả** tên
 * người dùng lẫn hàng nút. Bốn nút ăn ~128px, nên tên bị cắt còn `Nguy…` và vai
 * trò còn `SUPER…` — hai mẩu chữ không nói được gì. Một nút `⋮` trả lại ~96px,
 * đủ cho tên đầy đủ.
 *
 * ❌ **Không** dùng `MenuThaoTac`: component đó gắn với hành động trên **dòng
 * bảng** — nó nhận `tenBanGhi`, chia nhóm `xem`/`sua`/`pha-huy` và `stopPropagation`
 * để không mở khung chi tiết của dòng. Menu tài khoản không có dòng nào, nên
 * mượn nó là mang theo một đống ngữ nghĩa sai. Cả hai cùng dựng trên primitive
 * `DropdownMenu`, nên hình thức vẫn thống nhất.
 */
export function MenuTaiKhoan({
  collapsed,
  tenDayDu,
}: {
  collapsed: boolean;
  /** Tên người dùng, chỉ để ghép vào nhãn trợ năng. */
  tenDayDu: string;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const phamVi = usePhamVi();
  const logout = useAuthStore((s) => s.logout);
  const { ngonNgu, setNgonNgu } = useNgonNgu();
  const { t } = useT();
  const [moRong, setMoRong] = useState(false);

  /** Biểu tượng của một mục, cùng cỡ với mọi mục khác trong menu. */
  const bieuTuong = (Icon: typeof Settings) => <Icon size={15} aria-hidden />;

  /*
   * Lối chuyển sang giao diện tenant, **chỉ cho nhân sự nền tảng**.
   *
   * Một chiều: tài khoản tenant không có mục này và cũng không vào được
   * `/admin` (`PhamViGuard`). Nhân sự nền tảng cần xem được phía tenant để
   * dựng màn và để hỗ trợ khách hàng, nhưng dữ liệu họ thấy vẫn rỗng vì họ
   * không thuộc tenant nào — đây là lối xem giao diện, không phải lối vào dữ
   * liệu (ADR-UI-0012).
   */
  const dangXemTenant = tach(pathname).phamVi === 'tenant';
  const mucChuyenCong: MenuProps['items'] =
    phamVi === 'nen-tang'
      ? [
          {
            key: 'chuyen-cong',
            icon: bieuTuong(ArrowLeftRight),
            label: dangXemTenant ? t('khung.veCongQuanTri') : t('khung.xemGiaoDienTenant'),
            onClick: () =>
              navigate(dangXemTenant ? TIEN_TO['nen-tang'] : TIEN_TO.tenant),
          },
          { type: 'divider' as const },
        ]
      : [];

  const items: MenuProps['items'] = [
    ...mucChuyenCong,
    {
      key: 'ngon-ngu',
      icon: bieuTuong(Languages),
      label: t('khung.ngonNgu'),
      /*
       * Ngôn ngữ là **submenu**, không phải hai mục phẳng cạnh nhau: ngôn ngữ
       * thứ ba sẽ tới (xem ADR-UI-0010), và lúc đó ba mục phẳng chen giữa
       * `Cài đặt` với `Đăng xuất` làm menu đọc như một danh sách không có thứ
       * bậc. Submenu giữ bốn dòng gốc dù có thêm bao nhiêu ngôn ngữ.
       */
      children: NGON_NGU.map((ma) => ({
        key: `ngon-ngu-${ma}`,
        label: (
          <span className="flex items-center gap-2">
            {/*
              Dấu kiểm giữ chỗ **cố định** cho cả mục chưa chọn (`invisible`),
              nếu không hai nhãn lệch ngang mỗi lần đổi lựa chọn.
            */}
            <Check
              size={14}
              aria-hidden
              className={cn('shrink-0 text-primary-600', ngonNgu !== ma && 'invisible')}
            />
            {/* Nhãn **không dịch**: mỗi ngôn ngữ tự giới thiệu bằng chính nó. */}
            <span>{NHAN_NGON_NGU[ma]}</span>
          </span>
        ),
        onClick: () => setNgonNgu(ma),
      })),
    },
    {
      key: 'cai-dat',
      icon: bieuTuong(Settings),
      label: t('khung.caiDatTaiKhoan'),
      onClick: () => navigate('/cai-dat'),
    },
    {
      key: 'giao-dien',
      icon: bieuTuong(Palette),
      label: t('khung.giaoDien'),
      onClick: () => navigate('/giao-dien'),
    },
    { type: 'divider' },
    {
      key: 'dang-xuat',
      icon: bieuTuong(LogOut),
      label: t('khung.dangXuat'),
      /*
       * Vạch phân cách + `danger` tách đăng xuất khỏi ba mục thiết lập: nó là
       * hành động duy nhất **rời khỏi** ứng dụng, và nằm ngay dưới ba mục vô
       * hại thì rất dễ bấm nhầm.
       */
      danger: true,
      onClick: () => {
        logout();
        // Có Keycloak thì `logout` tự chuyển sang trang đăng xuất của Keycloak rồi về /dang-nhap. Điều hướng ngay ở đây
        // làm màn đăng nhập tự đẩy sang Keycloak, chen trước lệnh đăng xuất: phiên còn sống nên vào lại luôn, phải
        // bấm Đăng xuất lần hai. Chỉ điều hướng khi chạy dữ liệu mẫu (không có Keycloak).
        if (!oidcEnabled()) navigate('/dang-nhap');
      },
    },
  ];

  const nhanTroNang = tenDayDu
    ? t('khung.menuTaiKhoanCua', { ten: tenDayDu })
    : t('khung.menuTaiKhoan');

  return (
    <DropdownMenu
      menu={{ items }}
      // Thanh gập thì menu bung sang phải, tránh tràn khỏi mép dưới màn hình.
      placement={collapsed ? 'topLeft' : 'topRight'}
      open={moRong}
      onOpenChange={setMoRong}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={moRong}
        /*
         * `title` **và** `aria-label` cho nút chỉ-có-icon (§13.7). Không bọc
         * `Tooltip`: antd `Dropdown` gắn handler lên con trực tiếp, chèn một
         * lớp nữa vào giữa làm mất handler mở menu.
         */
        title={t('khung.menuTaiKhoan')}
        aria-label={nhanTroNang}
        className={cn(
          'grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors',
          moRong
            ? 'bg-subtle text-content-primary'
            : 'text-content-muted hover:bg-subtle hover:text-content-primary',
        )}
      >
        <MoreVertical size={16} />
      </button>
    </DropdownMenu>
  );
}
